import React, { useMemo } from "react"
import { Provider } from "react-redux"
import { configureStore } from "@reduxjs/toolkit"
import globalReducer, { setState } from "@/store/globalSlice"
import { resolveBackgroundUrl } from "@/utils/gameBackgrounds"
import { IGuideMock } from "@/utils/guideMockGame"
import {
  GameTimersContext,
  createGameTimersStore,
} from "@/utils/gameTimers"
import Board from "@/Pages/Game/Board"
import Sidebar from "@/Pages/Game/Sidebar"
import SidebarLeft from "@/Pages/Game/SidebarLeft"

/**
 * The game screen the guide points at (Modals/GuideModal.tsx).
 *
 * This is not a drawing of the game — it is the game screen: the real Board,
 * the real sidebars, the real player cards, panels, badges and buttons, laid
 * out by the same code the live screen uses. What makes it a diagram rather
 * than a game is everything around it:
 *
 *   - it renders against its own store, preloaded with an invented game
 *     (utils/guideMockGame.ts). Nothing in here can read or write the real one;
 *   - the whole tree is `pointer-events-none` and `aria-hidden`, so nothing in
 *     it can be clicked, hovered or dispatched — the guide's own rings sit over
 *     the top and take the mouse, and a screen reader is given the guide's
 *     descriptions rather than a second copy of the game;
 *   - the board is passed `preview`, which is what keeps the physics dice, the
 *     dice sounds and the ticking turn clock out of a screen that is a picture
 *     (see Pages/Game/Board.tsx).
 *
 * It is laid out at the *real* viewport size and scaled down by the modal.
 * Every measurement on this screen is in vh/vw or in rem off a root font size
 * that itself tracks the window (index.css), so the only copy that is faithful
 * at any window size is one drawn at window size — a mock built to some smaller
 * fixed box would rearrange itself into a layout the player never sees. The
 * modal shrinks the finished thing with a transform, which scales the pixels
 * rather than re-running the layout.
 *
 * Duplicated ids (#board, #center, #cell-N, #piece-N, …) are deliberate and
 * harmless: the animation helpers all reach for their elements with
 * getElementById/querySelector, which returns the first match in document
 * order, and the live game is always earlier in the document than this portal.
 */

/** The mock's own store: same reducer as the app, its own state, nothing shared. */
const createGuideStore = () =>
  configureStore({
    reducer: { global: globalReducer },
    // The state below holds DOM nodes and React components, exactly like the
    // real store; the app's store turns these checks off path by path, and a
    // dead screen has nothing to gain from them.
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false, immutableCheck: false }),
  })

/**
 * The copy is the whole game screen rendered a second time, against a game no
 * server ever sent. If any part of it were to throw, it would take the real
 * game down with it — the guide is mounted inside the game screen — so it is
 * fenced off here. A guide that fails to draw is a lost diagram; a game that
 * fails to draw is a lost game.
 */
class GuideMockBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error) {
    console.error("Guide diagram failed to render", error)
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full w-full items-center justify-center font-poppins text-sm text-white/50">
          The diagram could not be drawn. Your game is unaffected — press Escape
          to go back to it.
        </div>
      )
    }
    return this.props.children
  }
}

const GuideGameMockScreen: React.FC<{ mock: IGuideMock }> = ({ mock }) => {
  const store = useMemo(() => {
    const guideStore = createGuideStore()
    guideStore.dispatch(setState(mock.state))
    return guideStore
  }, [mock])

  // The clocks live outside redux (utils/gameTimers), so its own store is not
  // enough to keep the diagram off the live game's numbers — it needs its own
  // set of those too. Nothing ever writes to this one, which is what makes the
  // ring on the mock board a still 21 rather than a countdown.
  const timers = useMemo(
    () => createGameTimersStore(mock.state.myGame?.timers || {}),
    [mock]
  )

  return (
    <Provider store={store}>
     <GameTimersContext.Provider value={timers}>
      {/* The same root as Pages/Game/index.tsx, minus everything that is not
          part of the picture: the modals, the money animations, the voice chat
          and the notification layers. */}
      <div
        aria-hidden="true"
        className="guide-mock w-screen h-screen bg-cover grid-cols-[1fr_auto_1fr] grid overflow-hidden select-none pointer-events-none [-webkit-user-drag:none] [&_img]:select-none [&_img]:[-webkit-user-drag:none]"
        style={{
          backgroundImage: `url(${resolveBackgroundUrl(
            mock.state.selectedTheme?.id
          )})`,
        }}
      >
        <SidebarLeft />
        <Board preview />
        <Sidebar />
      </div>
     </GameTimersContext.Provider>
    </Provider>
  )
}

const GuideGameMock: React.FC<{ mock: IGuideMock }> = ({ mock }) => (
  <GuideMockBoundary>
    <GuideGameMockScreen mock={mock} />
  </GuideMockBoundary>
)

export default GuideGameMock
